import { createContext, useContext, useEffect, useCallback, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

interface PremiumContextType {
  /** Server-verified premium status (use this for gating) */
  isPremium: boolean;
  /** Whether the initial server check has completed */
  premiumLoading: boolean;
  setPremium: (value: boolean) => void;
  checkSubscription: () => Promise<void>;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  premiumLoading: true,
  setPremium: () => {},
  checkSubscription: async () => {},
});

export function PremiumProvider({ children }: { children: ReactNode }) {
  const { profile, session, refreshProfile } = useAuth();

  // Optimistic value from local profile (fast, but untrusted)
  const localPremium = profile?.plan === "premium";

  // Server-verified value (authoritative)
  const [verifiedPremium, setVerifiedPremium] = useState<boolean | null>(null);
  const [premiumLoading, setPremiumLoading] = useState(true);

  const refreshRef = useRef(refreshProfile);
  refreshRef.current = refreshProfile;

  // Verify premium status server-side
  const verifyPremium = useCallback(async () => {
    if (!session) {
      setVerifiedPremium(null);
      setPremiumLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke("check-premium");
      if (!error && data) {
        setVerifiedPremium(data.premium === true);
      }
    } catch {
      // On network error, fall back to local value (don't block UI)
    } finally {
      setPremiumLoading(false);
    }
  }, [session]);

  const checkSubscription = useCallback(async () => {
    if (!session) return;
    try {
      const { error } = await supabase.functions.invoke("check-subscription");
      if (!error) {
        await refreshRef.current();
        // Re-verify after Stripe sync
        await verifyPremium();
      }
    } catch {
      // silent
    }
  }, [session, verifyPremium]);

  // Verify on login + every 60s via Stripe sync
  useEffect(() => {
    if (!session) {
      setVerifiedPremium(null);
      setPremiumLoading(false);
      return;
    }

    // Initial: verify server-side + sync Stripe
    verifyPremium();
    checkSubscription();

    const interval = setInterval(checkSubscription, 300_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Use verified value when available, otherwise optimistic local value
  const isPremium = verifiedPremium !== null ? verifiedPremium : localPremium;

  return (
    <PremiumContext.Provider value={{ isPremium, premiumLoading, setPremium: () => {}, checkSubscription }}>
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  return useContext(PremiumContext);
}
